import { GroupReceiptDialogRenderer } from "$/components/universal-job-card/group-receipt-dialog-context"
import { useStoreId } from "$/hooks/use-store-information"
import jobQueries, {
  useBulkUpdateStatusMutation,
} from "$/lib/queries/job-queries"
import receiptQueries from "$/lib/queries/receipt-queries"
import {
  CombinedReceiptTotals,
  JobStatus,
  PriceUtils,
  Receipt,
  combineReceiptTotals,
} from "@repo/common"
import { PrintReceiptButton } from "@repo/ui/components/print-receipt-button"
import { ReceiptTotals } from "@repo/ui/components/receipt-components"
import { Button } from "@repo/ui/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/ui/dialog"
import { Body } from "@repo/ui/components/ui/typography"
import { UnifiedReceipt } from "@repo/ui/components/unified-receipt"
import { useToast } from "@repo/ui/hooks/use-toast"
import {
  SelectableCardDescription,
  SelectableCardLabel,
} from "@repo/ui/tanstack-form"
import { useQuery } from "@tanstack/react-query"
import { Loader2, ReceiptText } from "lucide-react"
import { useState } from "react"
import { useTranslations } from "use-intl"

interface Props {
  jobIds: string[]
  /** Called after every job has been marked as handed out. */
  onAllDelivered: () => void
  className?: string
}

export const CombinedReceiptButton = ({
  jobIds,
  onAllDelivered,
  className,
}: Props) => {
  const t = useTranslations("alfred.components.combinedReceipt")
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={className}
        onClick={() => setOpen(true)}
        disabled={jobIds.length === 0}
      >
        <ReceiptText className="size-4" />
        {t("viewCombinedReceipt")}
      </Button>
      {open && (
        <CombinedReceiptDialog
          jobIds={jobIds}
          onOpenChange={setOpen}
          onAllDelivered={onAllDelivered}
        />
      )}
    </>
  )
}

/** Opens the combined receipt for every job in a connected jobs group. */
export const ConnectedJobsGroupReceiptDialog = ({
  groupId,
  onOpenChange,
}: {
  groupId: string
  onOpenChange: (open: boolean) => void
}) => {
  const storeId = useStoreId()
  const { data: groupJobs } = useQuery(
    jobQueries.byConnectedJobsGroup(storeId, groupId),
  )

  return (
    <CombinedReceiptDialog
      jobIds={groupJobs?.map((job) => job.id) ?? []}
      onOpenChange={onOpenChange}
    />
  )
}

/** Provided to UniversalJobCard via GroupReceiptDialogRendererProvider. */
export const renderConnectedJobsGroupReceiptDialog: GroupReceiptDialogRenderer =
  (props) => <ConnectedJobsGroupReceiptDialog {...props} />

export const CombinedReceiptDialog = ({
  jobIds,
  onOpenChange,
  onAllDelivered,
}: {
  /** Empty while the caller is still loading the ids. */
  jobIds: string[]
  onOpenChange: (open: boolean) => void
  onAllDelivered?: () => void
}) => {
  const t = useTranslations("alfred.components.combinedReceipt")
  const storeId = useStoreId()
  const { toast } = useToast()
  const { data, isPending, isError } = useQuery({
    ...receiptQueries.combined(storeId, jobIds),
    enabled: jobIds.length > 0,
  })
  const bulkStatusMutation = useBulkUpdateStatusMutation(storeId, () => {
    toast({ title: t("markedDeliveredToast", { count: jobIds.length }) })
    onOpenChange(false)
    onAllDelivered?.()
  })

  const sections: CombinedReceiptSection[] = (data?.jobs ?? []).map((job) => ({
    id: job.jobId,
    heading: `${job.bikeLabel} · #${job.displayNumber}`,
    subheading: job.customerName,
    receipt: job.receipt,
    totalLabel: job.totalLabel,
    totalToPay: job.totalToPay,
  }))

  const handleMarkAllDelivered = () => {
    bulkStatusMutation.mutate({ jobIds, newStatus: JobStatus.DELIVERED })
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {jobIds.length > 0
              ? t("titleWithJobs", { count: jobIds.length })
              : t("title")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-6">
          {isPending && (
            <div className="flex justify-center py-8">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {isError && (
            <Body className="text-destructive">{t("loadFailed")}</Body>
          )}
          {data && <CombinedReceiptContent sections={sections} totals={data} />}
        </div>

        <DialogFooter className="gap-2">
          {data && (
            <PrintReceiptButton
              title={t("titleWithJobs", { count: jobIds.length })}
            >
              <CombinedReceiptContent sections={sections} totals={data} />
            </PrintReceiptButton>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("close")}
          </Button>
          <Button
            onClick={handleMarkAllDelivered}
            disabled={!data || data.allDelivered}
            loading={bulkStatusMutation.isPending}
          >
            {t("markAllDelivered")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export interface CombinedReceiptSection {
  id: string
  heading: string
  subheading: string | null
  /** Undefined when nothing has been added yet. */
  receipt: Receipt | undefined
  totalLabel: string
  totalToPay: number
}

/** One receipt per section plus a summary of all of them. */
const CombinedReceiptContent = ({
  sections,
  totals,
}: {
  sections: CombinedReceiptSection[]
  totals: CombinedReceiptTotals
}) => {
  const t = useTranslations("alfred.components.combinedReceipt")
  return (
    <>
      {sections.map((section, index) => (
        <div key={section.id} className="flex flex-col gap-2">
          {index > 0 && <DashedDivider />}
          <div className="flex flex-col min-w-0">
            <Body weight="semibold" className="truncate">
              {section.heading}
            </Body>
            {section.subheading !== null && (
              <Body size="s" className="text-muted-foreground truncate">
                {section.subheading}
              </Body>
            )}
          </div>
          {section.receipt ? (
            <UnifiedReceipt
              receipt={section.receipt}
              showBudget={false}
              showTotal={false}
              showHeaderSeparator={false}
              showVatRates
            />
          ) : (
            <Body size="s" className="text-muted-foreground">
              {t("noActions")}
            </Body>
          )}
          <div className="flex justify-between items-start gap-2">
            <SelectableCardDescription className="font-semibold">
              {section.totalLabel}
            </SelectableCardDescription>
            <SelectableCardDescription className="font-semibold shrink-0">
              {PriceUtils.formatWithoutCurrency(section.totalToPay)}
            </SelectableCardDescription>
          </div>
        </div>
      ))}
      <div className="flex flex-col gap-2 rounded-lg bg-secondary p-4">
        <div className="flex justify-between items-center gap-2">
          <Body weight="semibold">{t("summaryOfAllJobs")}</Body>
          <SelectableCardLabel className="shrink-0">
            {t("priceColumn")}
          </SelectableCardLabel>
        </div>
        <ReceiptTotals
          totalProductsCost={totals.productsCost}
          totalLaborPrice={totals.laborCost}
        />
        {totals.orderDiscountGiven > 0 && (
          <div className="flex justify-between items-start gap-2">
            <SelectableCardDescription>
              {t("discount")}
            </SelectableCardDescription>
            <SelectableCardDescription className="text-destructive-foreground">
              -{PriceUtils.formatWithoutCurrency(totals.orderDiscountGiven)}
            </SelectableCardDescription>
          </div>
        )}
        <div className="flex justify-between items-start gap-2">
          <SelectableCardDescription className="font-semibold">
            {t("total")}
          </SelectableCardDescription>
          <SelectableCardDescription className="font-semibold shrink-0">
            {PriceUtils.formatWithoutCurrency(totals.totalToPay)}
          </SelectableCardDescription>
        </div>
      </div>
    </>
  )
}

export interface ReceptionCombinedReceiptBike {
  /** E.g. "Längdskidor 2 (K2)". */
  label: string
  /** E.g. "Totalt för längdskidor 2". */
  totalLabel: string
  receipt: Receipt | undefined
}

/**
 * Combined receipt for the bikes in a reception that hasn't been saved yet.
 * `getBikes` is read when the dialog opens, so the active bike's receipt is
 * current.
 */
export const ReceptionCombinedReceiptButton = ({
  getBikes,
}: {
  getBikes: () => ReceptionCombinedReceiptBike[]
}) => {
  const t = useTranslations("alfred.components.combinedReceipt")
  const [bikes, setBikes] = useState<ReceptionCombinedReceiptBike[] | null>(
    null,
  )

  const sections: CombinedReceiptSection[] = (bikes ?? []).map(
    (bike, index) => ({
      id: String(index),
      heading: bike.label,
      subheading: null,
      receipt: bike.receipt,
      totalLabel: bike.totalLabel,
      totalToPay: bike.receipt
        ? (bike.receipt.totalCostAfterOrderDiscount ?? bike.receipt.totalCost)
        : 0,
    }),
  )
  const totals = combineReceiptTotals(
    sections.flatMap((section) =>
      section.receipt
        ? [{ receipt: section.receipt, totalToPay: section.totalToPay }]
        : [],
    ),
  )

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setBikes(getBikes())}
      >
        <ReceiptText className="size-4" />
        {t("title")}
      </Button>
      {bikes && (
        <Dialog open onOpenChange={(open) => !open && setBikes(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90dvh] flex flex-col">
            <DialogHeader>
              <DialogTitle>
                {t("titleWithBikes", { count: bikes.length })}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-6">
              <CombinedReceiptContent sections={sections} totals={totals} />
            </div>
            <DialogFooter className="gap-2">
              <PrintReceiptButton
                title={t("titleWithBikes", { count: bikes.length })}
              >
                <CombinedReceiptContent sections={sections} totals={totals} />
              </PrintReceiptButton>
              <Button variant="outline" onClick={() => setBikes(null)}>
                {t("close")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

// A gradient instead of border-dashed, whose dash length can't be set
const DashedDivider = () => (
  <div className="h-px mb-4 bg-[repeating-linear-gradient(to_right,hsl(var(--secondary-foreground)/0.5)_0_12px,transparent_12px_16px)]" />
)
